import Axios, { AxiosError } from 'axios'
import { BaseVideo, RootConfiguration } from '../pages/onboarding/types/types'
import { Document as RichTextDocument } from '@contentful/rich-text-types'

const ONBOARDING_URL =
    process.env.REACT_APP_ENV === 'development'
        ? '/v1/profiles/onboarding'
        : '/v1/profiles/onboarding'

export const ApiContent = Axios.create({
    baseURL: process.env.REACT_APP_CONTENT_API,
})

interface ApiErrorResponse {
    message: string;
    code?: number;
}

// Helper function to handle API errors
const handleApiError = (error: AxiosError<ApiErrorResponse>) => {
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        throw new Error(error.response.data?.message || 'Server error occurred')
    } else if (error.request) {
        // The request was made but no response was received
        throw new Error('No response received from server')
    } else {
        // Something happened in setting up the request that triggered an Error
        throw new Error(error.message || 'An unexpected error occurred')
    }
}

export const getOnboardingData = async (lang?: string) =>
    ApiContent.get<any>(ONBOARDING_URL, {
        headers: {
            'accept-language': lang || 'en',
        },
    })
    .then(result => result.data.data)
    .catch(handleApiError)

export const completeVideo = async (videoId: BaseVideo['id']) =>
    ApiContent.patch<RootConfiguration>('/v1/profiles/onboarding/video/watched', {
        videoId,
    })
    .then(({ data }) => {
        console.log('ApiContent: Patch getOnboardingData', data)
        return data
    })
    .catch(handleApiError)

export const getUserStudyPlan = () => 
    ApiContent.get<API<UserStudyPlanDTO>>(`v1/profiles/study-plans`)
    .then(({ data }) => {
        if (!data.data) {
            throw new Error('No study plan data received')
        }
        return data.data
    })
    .catch(handleApiError)

export const getContentStep = (stepId: string) =>
    ApiContent.get<API<ContentStepDTO>>(`v1/profiles/study-plans/step/${stepId}`)
    .then(({ data }) => {
        if (!data.data) {
            throw new Error('No content step data received')
        }
        return data.data
    })
    .catch(handleApiError)

// Interfaces
type API<TData> = {
    code: 200
    message: string
    data: TData
}

export interface UserStudyPlanDTO {
    id: string
    courseId: string
    language: string
    currentSessionId?: string
    sessions: {
        id: string
        title: string
        status: StudyPlanStatus
        number: number
        steps: {
            id: string
            title: string
            status: StudyPlanStatus
            nextStep: string
            prevStep: string
            duration: number | null
        }[]
    }[]
}

export type PlanSessionDTO = UserStudyPlanDTO['sessions'][0]
export type PlanStepDTO = PlanSessionDTO['steps'][0]

export type StudyPlanStatus = 'done' | 'inProgress' | 'blocked'

export interface ContentStepDTO {
    _id: string
    title: string
    principalMedia?: any
    slug?: string
    description: RichTextDocument
    suggestedExercise?: RichTextDocument
    duration: number | null
}
